import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Modal from 'react-modal';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FaCrown, FaCheckCircle, FaTimesCircle, FaClock, FaPlus, FaHistory, FaMoneyBillWave, FaTimes } from 'react-icons/fa';
import { updateSubscriptionAdmin, addPaymentAdmin, getPaymentsAdmin } from '../../services/api';
import regionConfig from '../../config/regionConfig';

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
    backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 620px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);

    .sub-modal-header {
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        padding: 20px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: white;

        .header-left {
            display: flex;
            align-items: center;
            gap: 12px;

            .header-icon {
                width: 40px;
                height: 40px;
                background: rgba(255,255,255,0.2);
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 18px;
            }

            .header-info {
                h3 { margin: 0; font-size: 17px; font-weight: 700; }
                p { margin: 2px 0 0; font-size: 12px; opacity: 0.85; }
            }
        }

        .close-btn {
            background: rgba(255,255,255,0.2);
            border: none;
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            transition: background 0.2s;

            &:hover { background: rgba(255,255,255,0.35); }
        }
    }

    .sub-modal-tabs {
        display: flex;
        border-bottom: 1px solid #e8ecf1;
        background: #fafbfc;

        button {
            flex: 1;
            padding: 12px 8px;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 13px;
            font-weight: 600;
            color: #888;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            border-bottom: 2px solid transparent;
            transition: all 0.2s;

            &:hover { color: rgb(49, 140, 213); background: rgba(49, 140, 213, 0.03); }
            &.active {
                color: rgb(49, 140, 213);
                border-bottom-color: rgb(49, 140, 213);
                background: white;
            }
        }
    }

    .sub-modal-body {
        padding: 24px;
        overflow-y: auto;
        flex: 1;
    }

    .sub-status-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 12px;
        border-radius: 16px;
        font-size: 12px;
        font-weight: 600;

        &.active { background: #e6f9ee; color: #0d7a3e; }
        &.trial { background: #e8f4fd; color: #1a73e8; }
        &.expired, &.inativo { background: #fce4ec; color: #c62828; }
        &.suspenso { background: #fff3e0; color: #e65100; }
    }

    .sub-info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-bottom: 24px;

        .sub-info-item {
            background: #f8f9fb;
            border-radius: 10px;
            padding: 14px 16px;

            .label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
            .value { font-size: 15px; font-weight: 600; color: #333; }
            .value.expired { color: #c62828; }
            .value.warning { color: #e65100; }
        }
    }

    .sub-section-title {
        font-size: 13px;
        font-weight: 700;
        color: #555;
        margin-bottom: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .sub-quick-actions {
        display: flex;
        gap: 8px;
        margin-bottom: 20px;
        flex-wrap: wrap;

        button {
            padding: 8px 16px;
            border: 1px solid #e0e0e0;
            background: white;
            border-radius: 8px;
            cursor: pointer;
            font-size: 13px;
            font-weight: 600;
            color: #555;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            gap: 4px;

            &:hover {
                border-color: rgb(49, 140, 213);
                color: rgb(49, 140, 213);
                background: rgba(49, 140, 213, 0.04);
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }

    .sub-custom-time {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-bottom: 20px;

        input {
            width: 80px;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 14px;
            text-align: center;

            &:focus { outline: none; border-color: rgb(49, 140, 213); }
        }

        select {
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 14px;
            background: white;
            cursor: pointer;

            &:focus { outline: none; border-color: rgb(49, 140, 213); }
        }

        .add-btn {
            padding: 8px 18px;
            background: rgb(49, 140, 213);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            white-space: nowrap;

            &:hover { background: rgb(79, 172, 254); }
            &:disabled { opacity: 0.5; cursor: not-allowed; }
        }
    }

    .sub-status-selector {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-bottom: 20px;

        select {
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 14px;
            background: white;
            flex: 1;
            cursor: pointer;

            &:focus { outline: none; border-color: rgb(49, 140, 213); }
        }

        .update-btn {
            padding: 8px 18px;
            background: #333;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            white-space: nowrap;

            &:hover { background: #555; }
            &:disabled { opacity: 0.5; cursor: not-allowed; }
        }
    }

    .sub-set-date {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-bottom: 16px;

        input[type="date"] {
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 14px;
            flex: 1;

            &:focus { outline: none; border-color: rgb(49, 140, 213); }
        }

        .set-date-btn {
            padding: 8px 18px;
            background: #6c757d;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            white-space: nowrap;

            &:hover { background: #868e96; }
            &:disabled { opacity: 0.5; cursor: not-allowed; }
        }
    }

    /* Payment Form */
    .sub-form-group {
        margin-bottom: 16px;

        label {
            display: block;
            font-size: 13px;
            font-weight: 600;
            color: #555;
            margin-bottom: 6px;
        }

        input, select, textarea {
            width: 100%;
            padding: 10px 14px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 14px;
            transition: border-color 0.2s;
            box-sizing: border-box;

            &:focus { outline: none; border-color: rgb(49, 140, 213); }
        }

        textarea { resize: vertical; min-height: 60px; }
    }

    .sub-form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .sub-form-checkbox {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;

        input[type="checkbox"] {
            width: 16px;
            height: 16px;
            cursor: pointer;
        }

        label {
            font-size: 13px;
            color: #555;
            cursor: pointer;
            margin: 0;
        }
    }

    .sub-submit-btn {
        width: 100%;
        padding: 12px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 10px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        &:hover { box-shadow: 0 4px 12px rgba(49, 140, 213, 0.3); }
        &:disabled { opacity: 0.5; cursor: not-allowed; }
    }

    /* History Table */
    .sub-history-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 13px;

        th {
            background: #f8f9fb;
            padding: 10px 12px;
            text-align: left;
            font-weight: 600;
            color: #666;
            border-bottom: 1px solid #e8ecf1;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
        }

        td {
            padding: 10px 12px;
            border-bottom: 1px solid #f0f0f0;
            color: #444;
        }

        tr:last-child td { border-bottom: none; }
    }

    .sub-empty-state {
        text-align: center;
        padding: 40px 20px;
        color: #999;
        font-size: 14px;
    }

    .sub-history-pagination {
        display: flex;
        justify-content: center;
        gap: 6px;
        margin-top: 16px;

        button {
            padding: 6px 12px;
            border: 1px solid #ddd;
            background: white;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            color: #666;

            &:hover:not(:disabled) { border-color: rgb(49, 140, 213); color: rgb(49, 140, 213); }
            &:disabled { opacity: 0.4; cursor: not-allowed; }
            &.active { background: rgb(49, 140, 213); color: white; border-color: rgb(49, 140, 213); }
        }
    }
`;

const getStatusClass = (status) => {
    switch (status) {
        case 'ativo': return 'active';
        case 'trial': return 'trial';
        case 'expirado':
        case 'inativo': return 'inativo';
        case 'suspenso': return 'suspenso';
        default: return 'trial';
    }
};

const ModalSubscriptionAdmin = ({ isOpen, onClose, empresa, onUpdate }) => {
    const { t } = useTranslation('subscription');
    const [activeTab, setActiveTab] = useState('status');
    const [loading, setLoading] = useState(false);

    // Status tab state
    const [customQty, setCustomQty] = useState(30);
    const [customUnit, setCustomUnit] = useState('days');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [setExpiryDate, setSetExpiryDate] = useState('');

    // Payment tab state
    const [paymentForm, setPaymentForm] = useState({
        amount: '',
        currency: regionConfig.currency.code || 'USD',
        payment_method: 'cash',
        period_quantity: 30,
        period_unit: 'days',
        extend_license: true,
        payment_date: moment().format('YYYY-MM-DD'),
        notes: '',
    });

    // History tab state
    const [payments, setPayments] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Derived data
    const statusPlano = empresa?.status_plano || empresa?.statusPlano || 'trial';
    const dataVencimento = empresa?.data_vencimento_licenca;
    const diasParaExpirar = dataVencimento
        ? moment(dataVencimento).diff(moment(), 'days')
        : null;

    useEffect(() => {
        if (isOpen && empresa) {
            setSelectedStatus(statusPlano);
            setActiveTab('status');
        }
    }, [isOpen, empresa]);

    const fetchPayments = useCallback(async (page = 1) => {
        if (!empresa?._id) return;
        setHistoryLoading(true);
        try {
            const response = await getPaymentsAdmin(empresa._id, page, 10);
            setPayments(response.data.payments || []);
            setHistoryTotal(response.data.total || 0);
            setHistoryPage(page);
        } catch (error) {
            console.error('[ADMIN-SUB] Error fetching payments:', error);
        } finally {
            setHistoryLoading(false);
        }
    }, [empresa?._id]);

    useEffect(() => {
        if (isOpen && activeTab === 'history' && empresa?._id) {
            fetchPayments(1);
        }
    }, [isOpen, activeTab, empresa?._id, fetchPayments]);

    // Quick action: add time
    const handleAddTime = async (quantity, unit) => {
        if (!empresa?._id) return;
        setLoading(true);
        try {
            await updateSubscriptionAdmin(empresa._id, {
                add_time: { quantity, unit }
            });
            toast.success(t('adminSubscription.successTimeAdded'));
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('[ADMIN-SUB] Error adding time:', error);
            toast.error(t('adminSubscription.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    // Update status
    const handleUpdateStatus = async () => {
        if (!empresa?._id || !selectedStatus) return;
        setLoading(true);
        try {
            await updateSubscriptionAdmin(empresa._id, {
                status_plano: selectedStatus
            });
            toast.success(t('adminSubscription.successStatusUpdated'));
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('[ADMIN-SUB] Error updating status:', error);
            toast.error(t('adminSubscription.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    // Set expiry date
    const handleSetExpiryDate = async () => {
        if (!empresa?._id || !setExpiryDate) return;
        setLoading(true);
        try {
            await updateSubscriptionAdmin(empresa._id, {
                set_expiry_date: setExpiryDate
            });
            toast.success(t('adminSubscription.successTimeAdded'));
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('[ADMIN-SUB] Error setting expiry date:', error);
            toast.error(t('adminSubscription.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    // Record payment
    const handleRecordPayment = async (e) => {
        e.preventDefault();
        if (!empresa?._id || !paymentForm.amount) return;
        setLoading(true);
        try {
            await addPaymentAdmin(empresa._id, {
                amount: parseFloat(paymentForm.amount),
                currency: paymentForm.currency,
                payment_method: paymentForm.payment_method,
                period_added: {
                    quantity: parseInt(paymentForm.period_quantity),
                    unit: paymentForm.period_unit,
                },
                extend_license: paymentForm.extend_license,
                payment_date: paymentForm.payment_date,
                notes: paymentForm.notes,
            });
            toast.success(t('adminSubscription.successPaymentRecorded'));
            // Reset form
            setPaymentForm(prev => ({
                ...prev,
                amount: '',
                notes: '',
                payment_date: moment().format('YYYY-MM-DD'),
            }));
            if (onUpdate) onUpdate();
            // Refresh history if on that tab
            if (activeTab === 'history') fetchPayments(1);
        } catch (error) {
            console.error('[ADMIN-SUB] Error recording payment:', error);
            toast.error(t('adminSubscription.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !empresa) return null;

    const historyTotalPages = Math.ceil(historyTotal / 10);

    return (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
            <ModalContainer>
                {/* Header */}
                <div className="sub-modal-header">
                    <div className="header-left">
                        <div className="header-icon"><FaCrown /></div>
                        <div className="header-info">
                            <h3>{t('adminSubscription.modalTitle')}</h3>
                            <p>{empresa.name || empresa.razao_social || empresa.nome_fantasia}</p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onClose}><FaTimes /></button>
                </div>

                {/* Tabs */}
                <div className="sub-modal-tabs">
                    <button
                        className={activeTab === 'status' ? 'active' : ''}
                        onClick={() => setActiveTab('status')}
                    >
                        <FaCheckCircle /> {t('adminSubscription.tabStatus')}
                    </button>
                    <button
                        className={activeTab === 'payment' ? 'active' : ''}
                        onClick={() => setActiveTab('payment')}
                    >
                        <FaMoneyBillWave /> {t('adminSubscription.tabPayment')}
                    </button>
                    <button
                        className={activeTab === 'history' ? 'active' : ''}
                        onClick={() => setActiveTab('history')}
                    >
                        <FaHistory /> {t('adminSubscription.tabHistory')}
                    </button>
                </div>

                {/* Body */}
                <div className="sub-modal-body">
                    {/* TAB: Status & Control */}
                    {activeTab === 'status' && (
                        <div>
                            {/* Info Grid */}
                            <div className="sub-info-grid">
                                <div className="sub-info-item">
                                    <div className="label">{t('adminSubscription.currentStatus')}</div>
                                    <div className="value">
                                        <span className={`sub-status-badge ${getStatusClass(statusPlano)}`}>
                                            {statusPlano === 'ativo' ? t('adminSubscription.statusActive')
                                                : statusPlano === 'trial' ? t('adminSubscription.statusTrial')
                                                : statusPlano === 'inativo' ? t('adminSubscription.statusInactive')
                                                : statusPlano === 'suspenso' ? t('adminSubscription.statusSuspended')
                                                : statusPlano}
                                        </span>
                                    </div>
                                </div>
                                <div className="sub-info-item">
                                    <div className="label">{t('adminSubscription.daysRemaining')}</div>
                                    <div className={`value ${diasParaExpirar !== null && diasParaExpirar < 0 ? 'expired' : diasParaExpirar !== null && diasParaExpirar <= 7 ? 'warning' : ''}`}>
                                        {diasParaExpirar !== null ? diasParaExpirar : '-'}
                                    </div>
                                </div>
                                <div className="sub-info-item" style={{ gridColumn: '1 / -1' }}>
                                    <div className="label">{t('adminSubscription.expiryDate')}</div>
                                    <div className="value">
                                        {dataVencimento
                                            ? moment(dataVencimento).format('DD/MM/YYYY HH:mm')
                                            : t('adminSubscription.noExpiry')}
                                    </div>
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="sub-section-title">{t('adminSubscription.quickActions')}</div>
                            <div className="sub-quick-actions">
                                <button onClick={() => handleAddTime(30, 'days')} disabled={loading}>
                                    <FaPlus /> {t('adminSubscription.addDays')}
                                </button>
                                <button onClick={() => handleAddTime(6, 'months')} disabled={loading}>
                                    <FaPlus /> {t('adminSubscription.addMonths')}
                                </button>
                                <button onClick={() => handleAddTime(1, 'years')} disabled={loading}>
                                    <FaPlus /> {t('adminSubscription.addYear')}
                                </button>
                            </div>

                            {/* Custom Time */}
                            <div className="sub-section-title">{t('adminSubscription.customTime')}</div>
                            <div className="sub-custom-time">
                                <input
                                    type="number"
                                    min="1"
                                    value={customQty}
                                    onChange={(e) => setCustomQty(parseInt(e.target.value) || 1)}
                                />
                                <select value={customUnit} onChange={(e) => setCustomUnit(e.target.value)}>
                                    <option value="days">{t('adminSubscription.days')}</option>
                                    <option value="months">{t('adminSubscription.months')}</option>
                                    <option value="years">{t('adminSubscription.years')}</option>
                                </select>
                                <button
                                    className="add-btn"
                                    onClick={() => handleAddTime(customQty, customUnit)}
                                    disabled={loading}
                                >
                                    {t('adminSubscription.addTime')}
                                </button>
                            </div>

                            {/* Set Expiry Date */}
                            <div className="sub-section-title">{t('adminSubscription.setExpiryDate')}</div>
                            <div className="sub-set-date">
                                <input
                                    type="date"
                                    value={setExpiryDate}
                                    onChange={(e) => setSetExpiryDate(e.target.value)}
                                />
                                <button
                                    className="set-date-btn"
                                    onClick={handleSetExpiryDate}
                                    disabled={loading || !setExpiryDate}
                                >
                                    {t('adminSubscription.setDate')}
                                </button>
                            </div>

                            {/* Update Status */}
                            <div className="sub-section-title">{t('adminSubscription.planStatus')}</div>
                            <div className="sub-status-selector">
                                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                                    <option value="ativo">{t('adminSubscription.statusActive')}</option>
                                    <option value="trial">{t('adminSubscription.statusTrial')}</option>
                                    <option value="inativo">{t('adminSubscription.statusInactive')}</option>
                                    <option value="suspenso">{t('adminSubscription.statusSuspended')}</option>
                                </select>
                                <button
                                    className="update-btn"
                                    onClick={handleUpdateStatus}
                                    disabled={loading}
                                >
                                    {t('adminSubscription.updateStatus')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* TAB: Record Payment */}
                    {activeTab === 'payment' && (
                        <form onSubmit={handleRecordPayment}>
                            <div className="sub-form-row">
                                <div className="sub-form-group">
                                    <label>{t('adminSubscription.amount')}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={paymentForm.amount}
                                        onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                                        required
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="sub-form-group">
                                    <label>{t('adminSubscription.currency')}</label>
                                    <select
                                        value={paymentForm.currency}
                                        onChange={(e) => setPaymentForm(prev => ({ ...prev, currency: e.target.value }))}
                                    >
                                        <option value="USD">USD ($)</option>
                                        <option value="BRL">BRL (R$)</option>
                                        <option value="EUR">EUR (&euro;)</option>
                                        <option value="LBP">LBP (ل.ل)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="sub-form-group">
                                <label>{t('adminSubscription.paymentMethod')}</label>
                                <select
                                    value={paymentForm.payment_method}
                                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                                >
                                    <option value="cash">{t('adminSubscription.cash')}</option>
                                    <option value="bank_transfer">{t('adminSubscription.bankTransfer')}</option>
                                    <option value="card">{t('adminSubscription.card')}</option>
                                    <option value="other">{t('adminSubscription.other')}</option>
                                </select>
                            </div>

                            <div className="sub-section-title">{t('adminSubscription.periodToAdd')}</div>
                            <div className="sub-form-row">
                                <div className="sub-form-group">
                                    <label>{t('adminSubscription.quantity')}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={paymentForm.period_quantity}
                                        onChange={(e) => setPaymentForm(prev => ({ ...prev, period_quantity: e.target.value }))}
                                        required
                                    />
                                </div>
                                <div className="sub-form-group">
                                    <label>{t('adminSubscription.unit')}</label>
                                    <select
                                        value={paymentForm.period_unit}
                                        onChange={(e) => setPaymentForm(prev => ({ ...prev, period_unit: e.target.value }))}
                                    >
                                        <option value="days">{t('adminSubscription.days')}</option>
                                        <option value="months">{t('adminSubscription.months')}</option>
                                        <option value="years">{t('adminSubscription.years')}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="sub-form-checkbox">
                                <input
                                    type="checkbox"
                                    id="extend-license"
                                    checked={paymentForm.extend_license}
                                    onChange={(e) => setPaymentForm(prev => ({ ...prev, extend_license: e.target.checked }))}
                                />
                                <label htmlFor="extend-license">{t('adminSubscription.extendLicense')}</label>
                            </div>

                            <div className="sub-form-group">
                                <label>{t('adminSubscription.paymentDate')}</label>
                                <input
                                    type="date"
                                    value={paymentForm.payment_date}
                                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                                />
                            </div>

                            <div className="sub-form-group">
                                <label>{t('adminSubscription.notes')}</label>
                                <textarea
                                    value={paymentForm.notes}
                                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder={t('adminSubscription.notesPlaceholder')}
                                />
                            </div>

                            <button type="submit" className="sub-submit-btn" disabled={loading}>
                                <FaMoneyBillWave />
                                {t('adminSubscription.recordPayment')}
                            </button>
                        </form>
                    )}

                    {/* TAB: Payment History */}
                    {activeTab === 'history' && (
                        <div>
                            {historyLoading ? (
                                <div className="sub-empty-state">{t('adminSubscription.loading')}</div>
                            ) : payments.length === 0 ? (
                                <div className="sub-empty-state">
                                    <FaHistory style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }} />
                                    <p>{t('adminSubscription.noPayments')}</p>
                                </div>
                            ) : (
                                <>
                                    <table className="sub-history-table">
                                        <thead>
                                            <tr>
                                                <th>{t('adminSubscription.historyDate')}</th>
                                                <th>{t('adminSubscription.historyAmount')}</th>
                                                <th>{t('adminSubscription.historyMethod')}</th>
                                                <th>{t('adminSubscription.historyPeriod')}</th>
                                                <th>{t('adminSubscription.historyNotes')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.map((payment, idx) => (
                                                <tr key={payment._id || idx}>
                                                    <td>{moment(payment.payment_date || payment.createdAt).format('DD/MM/YYYY')}</td>
                                                    <td style={{ fontWeight: 600 }}>
                                                        {/* Intl deriva simbolo E casas decimais do codigo da moeda.
                                                            O ternario anterior nao cobria LBP (que o select acima ja
                                                            oferece), entao pagamento em lira saia sem simbolo nenhum,
                                                            e o toFixed(2) fixo daria "895000.00" numa moeda sem centavos. */}
                                                        {new Intl.NumberFormat(undefined, {
                                                            style: 'currency',
                                                            currency: payment.currency || 'USD',
                                                        }).format(payment.amount || 0)}
                                                    </td>
                                                    <td style={{ textTransform: 'capitalize' }}>
                                                        {payment.payment_method === 'cash' ? t('adminSubscription.cash')
                                                            : payment.payment_method === 'bank_transfer' ? t('adminSubscription.bankTransfer')
                                                            : payment.payment_method === 'card' ? t('adminSubscription.card')
                                                            : t('adminSubscription.other')}
                                                    </td>
                                                    <td>
                                                        {payment.period_added?.quantity} {payment.period_added?.unit === 'days' ? t('adminSubscription.days')
                                                            : payment.period_added?.unit === 'months' ? t('adminSubscription.months')
                                                            : t('adminSubscription.years')}
                                                    </td>
                                                    <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {payment.notes || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {historyTotalPages > 1 && (
                                        <div className="sub-history-pagination">
                                            <button
                                                onClick={() => fetchPayments(historyPage - 1)}
                                                disabled={historyPage <= 1}
                                            >
                                                &laquo;
                                            </button>
                                            {Array.from({ length: historyTotalPages }, (_, i) => i + 1).map(p => (
                                                <button
                                                    key={p}
                                                    className={p === historyPage ? 'active' : ''}
                                                    onClick={() => fetchPayments(p)}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => fetchPayments(historyPage + 1)}
                                                disabled={historyPage >= historyTotalPages}
                                            >
                                                &raquo;
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default ModalSubscriptionAdmin;
